import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, BarChart3, ExternalLink, Search, Link2, User, AlertTriangle, Check } from "lucide-react";

const GAME_TYPES = ["league", "tournament", "playoff", "exhibition", "scrimmage", "showcase"];
const BAT_FIELDS = [
  ["pa", "PA"], ["ab", "AB"], ["r", "R"], ["h", "H"], ["doubles", "2B"], ["triples", "3B"],
  ["hr", "HR"], ["rbi", "RBI"], ["bb", "BB"], ["ibb", "IBB"], ["so", "SO"], ["hbp", "HBP"],
  ["sf", "SF"], ["sac", "SAC"], ["sb", "SB"], ["cs", "CS"], ["qab", "QAB"],
];
const PIT_FIELDS = [
  ["bf", "BF"], ["h", "H"], ["r", "R"], ["er", "ER"], ["bb", "BB"], ["so", "SO"],
  ["hbp", "HBP"], ["hr", "HR"], ["w", "W"], ["l", "L"], ["sv", "SV"], ["pitches", "Pit"], ["strikes", "Str"],
];

const outsToIp = (o) => `${Math.floor((o || 0) / 3)}.${(o || 0) % 3}`;
const ipToOuts = (s) => {
  const v = String(s ?? "").trim();
  if (!v) return 0;
  const [whole, frac = "0"] = v.split(".");
  const w = parseInt(whole || "0", 10) || 0;
  const f = Math.min(2, Math.max(0, parseInt(frac[0] || "0", 10) || 0));
  return w * 3 + f;
};
const singles = (b) => Math.max(0, (b.h || 0) - (b.doubles || 0) - (b.triples || 0) - (b.hr || 0));
const emptyForm = (pos) => ({
  entry_type: "player_only", game_id: null,
  date: new Date().toISOString().slice(0, 10), opponent_name: "", game_type: "league",
  location: "", event_name: "",
  did_bat: true, did_pitch: false, positions_played: pos || "",
  batting: {}, pitching: {}, ip: "", errors: 0,
  played_for_cbr_nation: false, team_id: "", needs_review: false,
});

export default function AdminPlayerGameStats({ playerId, primaryPosition }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm(primaryPosition));
  const [busy, setBusy] = useState(false);
  const [nationTeams, setNationTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  // existing-game search
  const [gameSearch, setGameSearch] = useState("");
  const [gameResults, setGameResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    api.get("/admin/cbr-nation/teams", { params: { context: "cbr_nation" } }).then(({ data }) => setNationTeams(data.items || [])).catch(() => {});
    api.get("/admin/cbr-nation/teams").then(({ data }) => setAllTeams(data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/players/${playerId}/game-stats`);
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  const resetSearch = () => { setGameSearch(""); setGameResults([]); setSelectedGame(null); };
  const openAdd = () => { setEditId(null); setForm(emptyForm(primaryPosition)); resetSearch(); setShowForm(true); };
  const openEdit = (r) => {
    setEditId(r.line_id);
    resetSearch();
    setForm({
      entry_type: "player_only", game_id: null,
      date: r.date || "", opponent_name: r.opponent_name || "", game_type: r.game_type || "league",
      location: r.location || "", event_name: r.event_name || "",
      did_bat: r.did_bat !== false, did_pitch: !!r.did_pitch,
      positions_played: (r.positions_played || []).join(", "),
      batting: r.batting || {}, pitching: r.pitching || {},
      ip: outsToIp((r.pitching || {}).ip_outs), errors: ((r.fielding || [])[0] || {}).e || 0,
      played_for_cbr_nation: r.participation_context === "cbr_nation", team_id: r.team_id || "",
      needs_review: r.participation_context === "needs_review",
    });
    setShowForm(true);
  };

  const setBat = (k, v) => setForm((f) => ({ ...f, batting: { ...f.batting, [k]: v === "" ? 0 : Number(v) } }));
  const setPit = (k, v) => setForm((f) => ({ ...f, pitching: { ...f.pitching, [k]: v === "" ? 0 : Number(v) } }));

  const runSearch = async () => {
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/players/${playerId}/game-search`, {
        params: { q: gameSearch || undefined, team_id: form.played_for_cbr_nation && form.team_id ? form.team_id : (form.team_id || undefined) },
      });
      setGameResults(data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSearching(false);
  };

  const buildPayload = () => {
    const positions = form.positions_played.split(",").map((s) => s.trim()).filter(Boolean);
    const b = {}; BAT_FIELDS.forEach(([k]) => { b[k] = Number(form.batting[k]) || 0; });
    const p = {}; PIT_FIELDS.forEach(([k]) => { p[k] = Number(form.pitching[k]) || 0; });
    if (form.did_pitch) p.ip_outs = ipToOuts(form.ip);
    const fielding = [];
    const eVal = Number(form.errors) || 0;
    if (eVal > 0) fielding.push({ position: positions[0] || primaryPosition || "UT", e: eVal });
    const base = {
      positions_played: positions,
      did_bat: form.did_bat, did_pitch: form.did_pitch,
      batting: form.did_bat ? b : {}, pitching: form.did_pitch ? p : {}, fielding,
    };
    if (form.entry_type === "existing_game") {
      return { ...base, entry_type: "existing_game", game_id: selectedGame?.game_id || form.game_id };
    }
    return {
      ...base, entry_type: "player_only",
      date: form.date, opponent_name: form.opponent_name, game_type: form.game_type,
      location: form.location || null, event_name: form.event_name || null,
      played_for_cbr_nation: form.played_for_cbr_nation,
      team_id: form.team_id || null, needs_review: form.needs_review,
    };
  };

  const validate = () => {
    if (editId) return true; // edit is always a player-only line
    if (form.entry_type === "existing_game") {
      if (!selectedGame) { toast.error("Search and select an existing game to attach this line to."); return false; }
      if (selectedGame.already_has_player_line) { toast.error("This player already has a stat line on that game."); return false; }
      return true;
    }
    if (!form.date) { toast.error("Game date is required."); return false; }
    if (!form.opponent_name.trim()) { toast.error("Opponent name is required."); return false; }
    if (form.played_for_cbr_nation && !form.team_id) { toast.error("Select a CBR Nation team — a flagged team is required."); return false; }
    if (!form.played_for_cbr_nation && !form.team_id && !form.needs_review) {
      toast.error("Select the represented team, or choose 'Team unknown — needs review'."); return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = buildPayload();
      if (editId) await api.patch(`/admin/players/${playerId}/game-stats/${editId}`, payload);
      else await api.post(`/admin/players/${playerId}/game-stats`, payload);
      toast.success(editId ? "Stat line updated"
        : form.entry_type === "existing_game" ? "Stat line attached to game"
        : "Player-only performance logged");
      setShowForm(false); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const del = async (r) => {
    if (!window.confirm("Delete this stat line? This updates the player's stats and rankings.")) return;
    try {
      await api.delete(`/admin/players/${playerId}/game-stats/${r.line_id}`);
      toast.success("Deleted"); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const num3 = (n) => (n === null || n === undefined ? "—" : Number(n).toFixed(3).replace(/^0/, ""));
  const isExisting = form.entry_type === "existing_game";

  return (
    <div className="mt-6" data-testid="admin-player-gamestats-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#E63946]" /> Player Performance Log
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Attach a stat line to an existing team game, or log a verified player-only performance.
            Player-only entries never create a team game or affect team records — they feed this player's
            Statistics, Game Log & leaderboards only.
          </p>
        </div>
        {!showForm && (
          <button data-testid="add-game-stat-btn" onClick={openAdd}
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-[#132a4d]">
            <Plus className="w-4 h-4" /> Log Player Performance
          </button>
        )}
      </div>

      {showForm && (
        <div className="mt-5 border border-slate-200 rounded-sm p-5 bg-slate-50" data-testid="game-stat-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#0A192F]">{editId ? "Edit Stat Line" : "Log Player Performance"}</h4>
            <button onClick={() => setShowForm(false)} data-testid="close-game-stat-form" className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>

          {/* Entry type selector (hidden while editing) */}
          {!editId && (
            <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="entry-type-selector">
              <button type="button" data-testid="entry-type-existing"
                onClick={() => { setForm((f) => ({ ...f, entry_type: "existing_game" })); }}
                className={`text-left border rounded-sm p-3 transition-colors ${isExisting ? "border-[#0A192F] bg-white ring-1 ring-[#0A192F]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#0A192F]">
                  <Link2 className="w-4 h-4" /> Attach to Existing Game
                  {isExisting && <Check className="w-4 h-4 text-[#E63946] ml-auto" />}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Add this player's line to a game already in the system. Does not create a game.</p>
              </button>
              <button type="button" data-testid="entry-type-player-only"
                onClick={() => { setForm((f) => ({ ...f, entry_type: "player_only" })); resetSearch(); }}
                className={`text-left border rounded-sm p-3 transition-colors ${!isExisting ? "border-[#0A192F] bg-white ring-1 ring-[#0A192F]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#0A192F]">
                  <User className="w-4 h-4" /> Player-Only Performance
                  {!isExisting && <Check className="w-4 h-4 text-[#E63946] ml-auto" />}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Verified individual stats only. No team game, schedule, record or ranking impact.</p>
              </button>
            </div>
          )}

          {/* Represented team / participation context (both modes, hidden on edit) */}
          {!editId && (
            <div className="mb-4 border border-slate-200 rounded-sm p-3 bg-white" data-testid="gs-affiliation">
              <label className="flex items-center gap-2 text-xs font-semibold text-[#0A192F] uppercase tracking-wider cursor-pointer">
                <input type="checkbox" data-testid="gs-cbr-nation-toggle" checked={form.played_for_cbr_nation}
                  onChange={(e) => setForm({ ...form, played_for_cbr_nation: e.target.checked, team_id: "", needs_review: false })} />
                Played for CBR Nation
              </label>
              <p className="text-[11px] text-slate-500 mt-1">
                {form.played_for_cbr_nation
                  ? "A team flagged as CBR Nation is required. The player's regular team is never substituted."
                  : "Regular club game. Confirm the team this player represented — it is not assumed from their profile."}
              </p>
              <div className="mt-2">
                {form.played_for_cbr_nation ? (
                  <select data-testid="gs-nation-team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                    className="w-full sm:w-96 border border-slate-300 rounded-sm px-2 py-1.5 text-sm">
                    <option value="">Select CBR Nation team…</option>
                    {nationTeams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.age_division ? ` (${t.age_division})` : ""}</option>)}
                  </select>
                ) : (
                  <select data-testid="gs-regular-team"
                    value={form.needs_review ? "__needs_review__" : form.team_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__needs_review__") setForm({ ...form, needs_review: true, team_id: "" });
                      else setForm({ ...form, needs_review: false, team_id: v });
                    }}
                    className="w-full sm:w-96 border border-slate-300 rounded-sm px-2 py-1.5 text-sm">
                    <option value="">Select represented team…</option>
                    {allTeams.filter((t) => !t.is_cbr_nation).map((t) => <option key={t.id} value={t.id}>{t.name}{t.age_division ? ` (${t.age_division})` : ""}</option>)}
                    <option value="__needs_review__">Team unknown — needs review</option>
                  </select>
                )}
                {form.played_for_cbr_nation && nationTeams.length === 0 && (
                  <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> No CBR Nation teams flagged. Mark a team as "CBR Nation Team" in Admin → Live Teams first — save is blocked until then.
                  </p>
                )}
                {!form.played_for_cbr_nation && form.needs_review && (
                  <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> This entry will be flagged "Team unresolved" for later review. No team record is affected.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* MODE A: Attach to existing game */}
          {!editId && isExisting && (
            <div className="mb-4 border border-slate-200 rounded-sm p-3 bg-white" data-testid="existing-game-panel">
              <div className="flex items-end gap-2">
                <label className="text-xs font-semibold text-slate-600 flex-1">Search existing games (opponent, tournament, location or date)
                  <input type="text" data-testid="game-search-input" value={gameSearch} placeholder="e.g. Canes National or 2026-10-12"
                    onChange={(e) => setGameSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
                <button type="button" data-testid="game-search-btn" onClick={runSearch} disabled={searching}
                  className="inline-flex items-center gap-1 bg-[#0A192F] text-white px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-[#132a4d] disabled:opacity-50">
                  <Search className="w-3.5 h-3.5" /> {searching ? "…" : "Search"}
                </button>
              </div>
              <div className="mt-3 max-h-56 overflow-y-auto divide-y divide-slate-100">
                {gameResults.length === 0 ? (
                  <p className="text-[11px] text-slate-400 py-3">No games loaded yet. Search to find a game to attach this line to.</p>
                ) : gameResults.map((g) => (
                  <button type="button" key={g.game_id} data-testid={`game-result-${g.game_id}`}
                    disabled={g.already_has_player_line}
                    onClick={() => setSelectedGame(g)}
                    className={`w-full text-left py-2 px-2 flex items-center justify-between gap-3 rounded-sm ${selectedGame?.game_id === g.game_id ? "bg-[#0A192F]/5 ring-1 ring-[#0A192F]" : "hover:bg-slate-50"} ${g.already_has_player_line ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <div>
                      <div className="text-sm font-semibold text-[#0A192F]">{g.matchup}</div>
                      <div className="text-[11px] text-slate-500">{g.date} · <span className="capitalize">{g.game_type}</span>{g.tournament ? ` · ${g.tournament}` : ""}{g.location ? ` · ${g.location}` : ""}</div>
                    </div>
                    {g.already_has_player_line
                      ? <span className="text-[9px] uppercase font-semibold text-amber-600">Line exists</span>
                      : selectedGame?.game_id === g.game_id
                        ? <Check className="w-4 h-4 text-[#E63946]" />
                        : <span className="text-[9px] uppercase font-semibold text-slate-400">Select</span>}
                  </button>
                ))}
              </div>
              {selectedGame && (
                <div className="mt-2 text-[11px] bg-[#0A192F] text-white rounded-sm px-3 py-2" data-testid="selected-game-summary">
                  Attaching to: <strong>{selectedGame.matchup}</strong> · {selectedGame.date}. A stat line will be added — no new game is created.
                </div>
              )}
            </div>
          )}

          {/* MODE B: Player-only game details */}
          {!editId && !isExisting && (
            <>
              <div className="grid sm:grid-cols-4 gap-3">
                <label className="text-xs font-semibold text-slate-600">Date
                  <input type="date" data-testid="gs-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
                <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Opponent
                  <input type="text" data-testid="gs-opponent" value={form.opponent_name} placeholder="e.g. Canes National"
                    onChange={(e) => setForm({ ...form, opponent_name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
                <label className="text-xs font-semibold text-slate-600">Game Type
                  <select data-testid="gs-type" value={form.game_type} onChange={(e) => setForm({ ...form, game_type: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal capitalize">
                    {GAME_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select></label>
                <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Tournament / Event (optional)
                  <input type="text" data-testid="gs-event" value={form.event_name} placeholder="e.g. WWBA Championship"
                    onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
                <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Location (optional)
                  <input type="text" data-testid="gs-location" value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
              </div>
              <div className="mt-3 text-[11px] bg-blue-50 border border-blue-100 text-blue-800 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="player-only-notice">
                <User className="w-4 h-4 mt-0.5 shrink-0" />
                This entry updates player statistics only. It will not create a team game or affect team records, schedules or rankings.
              </div>
            </>
          )}

          <div className="grid sm:grid-cols-4 gap-3 mt-3">
            <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Positions (comma sep)
              <input type="text" data-testid="gs-positions" value={form.positions_played} placeholder="SS, CF"
                onChange={(e) => setForm({ ...form, positions_played: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
            <label className="text-xs font-semibold text-slate-600">Errors (E)
              <input type="number" min="0" data-testid="gs-errors" value={form.errors}
                onChange={(e) => setForm({ ...form, errors: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-normal" /></label>
          </div>

          {/* Batting */}
          <div className="mt-5">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#0A192F] uppercase tracking-wider">
              <input type="checkbox" data-testid="gs-did-bat" checked={form.did_bat} onChange={(e) => setForm({ ...form, did_bat: e.target.checked })} /> Batting
            </label>
            {form.did_bat && (
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-2">
                {BAT_FIELDS.map(([k, lbl]) => (
                  <label key={k} className="text-[10px] font-semibold text-slate-500 uppercase">{lbl}
                    <input type="number" min="0" data-testid={`gs-bat-${k}`} value={form.batting[k] ?? ""}
                      onChange={(e) => setBat(k, e.target.value)}
                      className="mt-0.5 w-full border border-slate-300 rounded-sm px-1.5 py-1 text-sm font-normal text-center" /></label>
                ))}
                <div className="text-[10px] font-semibold text-slate-400 uppercase">1B (auto)
                  <div className="mt-0.5 w-full border border-slate-200 bg-slate-100 rounded-sm px-1.5 py-1 text-sm text-center text-slate-500">{singles(form.batting)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Pitching */}
          <div className="mt-5">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#0A192F] uppercase tracking-wider">
              <input type="checkbox" data-testid="gs-did-pitch" checked={form.did_pitch} onChange={(e) => setForm({ ...form, did_pitch: e.target.checked })} /> Pitching
            </label>
            {form.did_pitch && (
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase">IP
                  <input type="text" data-testid="gs-pit-ip" value={form.ip} placeholder="5.2"
                    onChange={(e) => setForm({ ...form, ip: e.target.value })}
                    className="mt-0.5 w-full border border-slate-300 rounded-sm px-1.5 py-1 text-sm font-normal text-center" /></label>
                {PIT_FIELDS.map(([k, lbl]) => (
                  <label key={k} className="text-[10px] font-semibold text-slate-500 uppercase">{lbl}
                    <input type="number" min="0" data-testid={`gs-pit-${k}`} value={form.pitching[k] ?? ""}
                      onChange={(e) => setPit(k, e.target.value)}
                      className="mt-0.5 w-full border border-slate-300 rounded-sm px-1.5 py-1 text-sm font-normal text-center" /></label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button data-testid="save-game-stat-btn" onClick={save} disabled={busy}
              className="bg-[#E63946] text-white px-5 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-[#c92b38] disabled:opacity-50">
              {busy ? "Saving…" : editId ? "Update Line" : isExisting ? "Attach Stat Line" : "Save Player-Only Entry"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800">Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="mt-6 overflow-x-auto">
        {loading ? <p className="text-sm text-slate-400 py-6">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-6" data-testid="no-game-stats">No performances logged yet. Log a performance to populate this player's profile & rankings.</p>
        ) : (
          <table className="w-full text-sm" data-testid="game-stats-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-200">
                <th className="py-2 pr-3">Date</th><th className="pr-3">Opponent</th><th className="pr-3">Type</th>
                <th className="pr-2 text-center">AB</th><th className="pr-2 text-center">H</th><th className="pr-2 text-center">HR</th>
                <th className="pr-2 text-center">RBI</th><th className="pr-2 text-center">BB</th><th className="pr-2 text-center">SB</th>
                <th className="pr-2 text-center">AVG</th><th className="pr-2 text-center">OPS</th>
                <th className="pr-2 text-center">IP</th><th className="pr-2 text-center">ER</th><th className="pr-2 text-center">SO</th><th className="pr-2 text-center">ERA</th>
                <th className="pr-2 text-center">Type</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const b = r.batting || {}, p = r.pitching || {};
                return (
                  <tr key={r.line_id} data-testid={`game-stat-row-${r.line_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 whitespace-nowrap">{r.date}</td>
                    <td className="pr-3">{r.opponent_name || "—"}</td>
                    <td className="pr-3 capitalize text-slate-500">{r.game_type}</td>
                    <td className="pr-2 text-center">{b.ab ?? 0}</td>
                    <td className="pr-2 text-center">{b.h ?? 0}</td>
                    <td className="pr-2 text-center">{b.hr ?? 0}</td>
                    <td className="pr-2 text-center">{b.rbi ?? 0}</td>
                    <td className="pr-2 text-center">{b.bb ?? 0}</td>
                    <td className="pr-2 text-center">{b.sb ?? 0}</td>
                    <td className="pr-2 text-center font-semibold">{num3(b.avg)}</td>
                    <td className="pr-2 text-center font-semibold">{num3(b.ops)}</td>
                    <td className="pr-2 text-center">{r.did_pitch ? outsToIp(p.ip_outs) : "—"}</td>
                    <td className="pr-2 text-center">{r.did_pitch ? (p.er ?? 0) : "—"}</td>
                    <td className="pr-2 text-center">{r.did_pitch ? (p.so ?? 0) : "—"}</td>
                    <td className="pr-2 text-center font-semibold">{r.did_pitch && p.era != null ? p.era.toFixed(2) : "—"}</td>
                    <td className="pr-2 text-center">
                      {r.is_player_only ? (
                        <span data-testid={`player-only-badge-${r.line_id}`} className="text-[9px] px-1.5 py-0.5 rounded-sm font-semibold uppercase bg-amber-100 text-amber-700" title="Verified player-only performance — no team result">Player-Only</span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-semibold uppercase bg-slate-100 text-slate-500">Team Game</span>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {r.editable ? (
                        <div className="flex items-center gap-2 justify-end">
                          <button data-testid={`edit-game-stat-${r.line_id}`} onClick={() => openEdit(r)} className="text-slate-400 hover:text-[#0A192F]"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`delete-game-stat-${r.line_id}`} onClick={() => del(r)} className="text-slate-400 hover:text-[#E63946]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <a href={`/games/${r.game_id}`} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-slate-600 inline-flex"><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
